import { placeLeagueResults } from '@league/shared/scoring';
import { useHowTo } from '../../components/HowTo';
import { scoringHowTo } from '../../content/how-to';
import { ReadCard, type CardReading } from './ReadCard';
import { doubtfulCells, ReadingNotice } from './ReadingNotice';
import { ScoreGrid } from './ScoreGrid';
import { fetchLeague, LeaguePage } from '../leagues/LeagueHeader';
import { emptyGames, gamesFromReading, gamesFromSaved, gridProblems, gridTotals, hasAnyEntry, matchesSaved, readButEmpty as emptyButRead, entriesSignature, gamesFromStorage, toEntries, type Game } from './grid';
import { StructuredTableCard } from './StructuredTableCard';
import type { SessionSheetResponse } from '@league/shared/contract';
import { LINK_CLASS } from '@league/ui';
import { GAMES_REQUIRED } from '@league/shared/scoring';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Button, ErrorNote } from '../../components/ui';
import { ApiError, apiFetch } from '../../lib/api';
import type { Actor } from '../../lib/auth';

/**
 * ENTERING A SESSION'S SCORES, on a phone, in a loud hall, at nine o'clock.
 *
 * She is holding East's paper card in one hand and this in the other. So the screen is built around
 * what the card actually says rather than around the database: ONE GAME PER ROW, because a game has
 * exactly one winner, and typing four zeroes for the people who did not win is three chances to
 * type the wrong thing.
 *
 * Each game is two taps and a number — who won, how much. A wall game is one tap and gives everyone
 * ten. Her running totals sit underneath and update as she types, with the four that count marked,
 * so "why is my 25 not in there" is answered on the screen instead of by her.
 *
 * NOTHING IS TYPED THAT CAN BE WORKED OUT. She never enters a session total; it is the best four of
 * what she entered. If she adds a fifth game later, every total moves on its own.
 *
 * THE DRAFT SURVIVES A RELOAD. Halls have bad signal and phones lock. Losing twenty minutes of
 * typing to a dropped connection is the one failure that would stop her using this at all, so the
 * table she is working on is kept in the browser until it saves.
 */





export function ScoreSheet({ actor }: { actor?: Actor }) {
  const { sessionId } = useParams<{ sessionId: string }>();
  const queryClient = useQueryClient();

  const [tableNumber, setTableNumber] = useState<number | null>(null);
  const [games, setGames] = useState<Game[]>([]);
  const [dirty, setDirty] = useState(false);
  const [draftProblem, setDraftProblem] = useState<string | null>(null);
  const [loadedDraftKey, setLoadedDraftKey] = useState<string | null>(null);
  const [draftRevision, setDraftRevision] = useState<string | undefined>();
  const [editingApprovedKey, setEditingApprovedKey] = useState<string | null>(null);
  const [structuredDirty, setStructuredDirty] = useState(false);
  /** What the last photo of this table's card said, until it is saved or she moves table. */
  const [reading, setReading] = useState<CardReading | null>(null);
  /** What just happened to this table, said once, until she edits or moves table. */
  const [done, setDone] = useState<{ table: number; kind: 'saved' | 'approved' | 'reapprove' } | null>(null);

  const sheet = useQuery({
    queryKey: ['scores', sessionId],
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    queryFn: () => apiFetch<SessionSheetResponse>(`/api/sessions/${sessionId}/scores`),
    enabled: Boolean(sessionId),
  });

  const howTo = useHowTo(scoringHowTo(sheet.data?.sheet.scoringMode));

  const tables = sheet.data?.sheet.tables ?? [];
  const table = tables.find((t) => t.tableNumber === tableNumber) ?? tables[0];

  /** The key the draft is kept under. Per session AND per table, so two tables cannot collide. */
  const draftKey = sessionId && table ? `scores:${sessionId}:${table.tableNumber}` : null;

  /**
   * Load what is already saved, or the draft she was in the middle of. The draft wins: it is by
   * definition newer than what the server has, since it only exists because she has not saved yet.
   */
  useEffect(() => {
    if (!table || !draftKey) return;

    setLoadedDraftKey(draftKey);
    const stored = readDraft(draftKey);
    const wrongPlayers = stored?.some(game => Object.keys(game.points).some(id => !table.players.some(player => player.playerId === id)));
    const draft = wrongPlayers ? null : stored;
    setDraftProblem(wrongPlayers ? 'This browser has a draft for different players. Saved scores are shown; the old draft has been kept for review.' : null);
    setDraftRevision(draft ? readDraftRevision(draftKey) : table.revision);
    if (draft) {
      setGames(draft);
      setDirty(true);
      return;
    }

    /*
      ⚠ AN UNTOUCHED TABLE OPENS WITH THE {@link GAMES_REQUIRED} COLUMNS A TABLE MUST FINISH.

      It used to open completely empty — a dashed "+ Game 1" box and nothing else — which hid the
      only thing that explains this screen: the grid. Four columns is not a guess, it is the number
      the rules require, and the printed card shows all eight for the same reason.

      Empty columns are NOT dirty and NOT saved. `toEntries` drops any column nobody has written
      in, so an untouched sheet still writes no scores at all.
    */
    const saved = gamesFromSaved(table.players);
    setGames(
      saved.length > 0
        ? saved
        : Array.from({ length: GAMES_REQUIRED }, (_, index) => ({
            gameNumber: index + 1,
            points: {},
          })),
    );
    setDirty(false);
  }, [table?.tableNumber, sheet.dataUpdatedAt, draftKey]);

  /*
    A reading belongs to ONE table's card. Not cleared on the 30-second refetch, which reruns the
    effect above — only when she actually moves to another table.
  */
  useEffect(() => setReading(null), [table?.tableNumber]);
  useEffect(() => setDone(null), [table?.tableNumber]);
  useEffect(() => {
    if (dirty) setDone(null);
  }, [dirty]);

  /** Kept on every keystroke rather than on a timer, because a phone locks without warning. */
  useEffect(() => {
    if (!draftKey || loadedDraftKey !== draftKey) return;
    if (dirty) writeDraft(draftKey, games, draftRevision);
  }, [games, dirty, draftKey, draftRevision, loadedDraftKey]);

  const refreshScores = () => Promise.all([
    queryClient.invalidateQueries({ queryKey: ['scores', sessionId] }),
    queryClient.invalidateQueries({ queryKey: ['league'] }),
    queryClient.invalidateQueries({ queryKey: ['director-dashboard'] }),
    queryClient.invalidateQueries({ queryKey: ['director-next-steps'] }),
  ]);

  const save = useMutation({
    mutationFn: async () => {
      if (!table) throw new Error('No table');
      const entries = toEntries(games, table.players);
      try {
        return await apiFetch<SessionSheetResponse>(
          `/api/sessions/${sessionId}/scores/${table.tableNumber}`,
          { method: 'PUT', body: JSON.stringify({ entries, revision: draftRevision }) },
        );
      } catch (err) {
        /*
          ⚠ ON A WEAK CONNECTION THE SAVE CAN ARRIVE AND ONLY THE ANSWER BE LOST (owner, 2026-10-14).
          Trying again is then refused as out of date, because her own save changed the table. So
          before saying it failed, ask what the server has: if it is exactly what she sent, it saved.
        */
        const fresh = await apiFetch<SessionSheetResponse>(`/api/sessions/${sessionId}/scores`).catch(() => null);
        const now = fresh?.sheet.tables.find((t) => t.tableNumber === table.tableNumber);
        if (now && matchesSaved(games, now.players)) return fresh;
        throw err;
      }
    },
    onSuccess: async () => {
      if (draftKey) clearDraft(draftKey);
      setDirty(false);
      setEditingApprovedKey(null);
      setReading(null);
      /*
        ⚠ SHE STAYS ON THIS TABLE — owner, 2026-10-04. Saving used to jump straight to the next
        table, unannounced, so her scores seemed to vanish and nothing said where they had gone.
        Now it says so, and "Next" is her choice. Saving an approved table clears the approval (the
        server's rule: an edit needs reading again), so that case says so too.
      */
      const wasApproved = table?.state === 'approved';
      await refreshScores();
      if (table) setDone({ table: table.tableNumber, kind: wasApproved ? 'reapprove' : 'saved' });
    },
  });

  const scoringBlocked = sheet.data?.sheet.blockedFromScoring ?? null;
  const closedAt = sheet.data?.sheet.closedAt ?? null;
  const blocked = sheet.data?.sheet.blockedFromClosing ?? null;

  /**
   * THE REVIEW HALF. Neither of these writes a score — the rows are already there, entered by
   * whoever entered them. Approving records that she read them; closing records that she is
   * finished with the session.
   */
  const approve = useMutation({
    mutationFn: async () => {
      if (!table) throw new Error('No table');
      return apiFetch<SessionSheetResponse>(
        `/api/sessions/${sessionId}/scores/${table.tableNumber}/approve`,
        { method: 'POST', body: JSON.stringify({ revision: table.revision }) },
      );
    },
    onSuccess: async () => {
      await refreshScores();
      /* Stays on this table and says so; "Next" is hers to press (see save, above). */
      if (table) setDone({ table: table.tableNumber, kind: 'approved' });
    },
  });

  const closeSession = useMutation({
    mutationFn: async (reopen: boolean) =>
      apiFetch<SessionSheetResponse>(
        `/api/sessions/${sessionId}/scores/${reopen ? 'reopen' : 'close'}`,
        { method: 'POST' },
      ),
    onSuccess: refreshScores,
  });

  const approvedReadOnly = table?.state === 'approved' && editingApprovedKey !== draftKey;
  const displayedGames = approvedReadOnly && table ? gamesFromSaved(table.players) : games;
  /* Games that cannot be saved as they stand (or still belong to the last table) count as changed. */
  const correctionChanged = !table || table.state !== 'approved' || entriesSignature(games, table.players) !== entriesSignature(gamesFromSaved(table.players), table.players);

  /** Running totals, worked out here exactly as the server will work them out. */
  const totals = useMemo(
    () => (table ? gridTotals(displayedGames, table.players, sheet.data?.sheet.scoringMode) : new Map<string, { points: number; counted: number[] }>()),
    [displayedGames, table, sheet.data?.sheet.scoringMode],
  );

  /** Games that break the one-scorer rule. The grid names them; Save waits until they are settled. */
  const problems = useMemo(() => (table ? gridProblems(games, table.players) : []), [games, table]);
  const empty = useMemo(() => emptyGames(games), [games]);

  /* Inside the league's own page frame, so the header and tabs sit where they do on every league page. */
  const leagueId = sheet.data?.sheet.leagueId;
  const frame = (content: React.ReactNode) =>
    leagueId ? <LeaguePage leagueId={leagueId} current="Scores">{content}</LeaguePage> : content;

  if (sheet.isPending) return <p className="px-6 py-10 text-sm text-ink-3">Loading…</p>;

  if (tables.length === 0) {
    return frame(
      <div className="max-w-2xl">
        <p className="mt-6 rounded-md border border-dashed border-stroke px-5 py-8 text-center text-[15px] text-ink-2">
          <Link to={`/director/sessions/${sessionId}/seating`} className={LINK_CLASS}>{scoringBlocked ?? "Finalize seating to enter scores"}</Link>
        </p>
      </div>
    );
  }

  if (!table) return null;

  if (sheet.data?.sheet.scoringMode && table.players.every(p => p.games.every(g => g.evidence)) && table.players.some(p => p.games.some(g => g.evidence?.result.outcome !== 'incomplete'))) return frame(<div className="flex max-w-2xl flex-col gap-4">
    <div>
      <div className="flex items-center gap-1">
        <h1 className="font-display text-2xl">{sheet.data.sheet.leagueName} — Session {sheet.data.sheet.sessionNumber}</h1>
        {howTo.button}
      </div>
      {howTo.panel}
    </div>
    <div className="flex flex-wrap gap-2">{tables.map(t=><Button key={t.tableNumber} variant="secondary" onClick={()=>setTableNumber(t.tableNumber)}>Table {t.tableNumber}</Button>)}</div>
    {scoringBlocked ? <ErrorNote>{scoringBlocked}</ErrorNote> : null}
    <section><h2 className="font-semibold">Session leaders — approved scores</h2><ul>{placeLeagueResults(tables.filter(t=>t.state==='approved').flatMap(t=>t.players).map(p=>({playerId:p.playerId,points:p.total,highestWinningHand:0,wins:0,deductions:0})),'session').filter(p=>p.winner).map(p=><li key={p.playerId}>{p.rank}. {tables.flatMap(t=>t.players).find(person=>person.playerId===p.playerId)?.firstName} — {p.points} points</li>)}</ul></section>
    <StructuredTableCard key={`${sessionId}:${table.tableNumber}`} director onDirtyChange={setStructuredDirty} mode={sheet.data.sheet.scoringMode} storageKey={`structured-director:${sessionId}:${table.tableNumber}`} revision={table.revision ?? ''} players={table.players.map(p=>({...p,name:`${p.firstName} ${p.lastName}`}))} editable={!closedAt && !scoringBlocked && !sheet.isError} state={table.state} onSave={async (entries,revision)=>{
      try { await apiFetch(`/api/sessions/${sessionId}/scores/${table.tableNumber}`,{method:'PUT',body:JSON.stringify({entries,revision})}); }
      finally { await refreshScores(); }
    }}/>
    {approve.error ? <ErrorNote>{approve.error.message}</ErrorNote> : null}
    {/* One primary: the card's save while editing (or Edit scores once approved), otherwise this. */}
    <Button variant={structuredDirty || table.state === 'approved' || table.state === 'open' ? 'secondary' : 'primary'} disabled={structuredDirty || approve.isPending || !table.complete || table.state==='approved' || Boolean(closedAt) || Boolean(scoringBlocked)} onClick={()=>approve.mutate()}>Finalize table scores</Button>
    {blocked ? <p className="text-sm text-ink-2">{blocked}</p> : null}
    {closeSession.error ? <ErrorNote>{closeSession.error.message}</ErrorNote> : null}
    <Button variant="secondary" disabled={structuredDirty || closeSession.isPending || (!closedAt && Boolean(blocked))} onClick={()=>closeSession.mutate(Boolean(closedAt))}>{closedAt?'Reopen session':'Close session scores'}</Button>
  </div>);

  const error = save.error instanceof TypeError || (save.error instanceof ApiError && (save.error.code === 'NO_BODY' || save.error.code === 'NETWORK')) ? 'Could not save. Your entries are still here. Check your connection and try again.' : save.error instanceof Error ? save.error.message : null;

  /** The next table that still needs her: scores first, then approval. */
  const nextTable =
    tables.find((t) => t.tableNumber > table.tableNumber && !t.complete) ??
    tables.find((t) => t.tableNumber !== table.tableNumber && !t.complete) ??
    tables.find((t) => t.tableNumber !== table.tableNumber && t.state !== 'approved');

  /**
   * A photo's reading becomes the grid, exactly as if she had typed it: an unsaved draft, kept in
   * the browser, held by the same one-scorer check, saved only by her pressing Save.
   */
  function fillFromCard(read: CardReading) {
    /*
      A BLANK READING FILLS NOTHING, so it must not empty what she already typed — the notice says
      "Nothing was filled in", and the grid used to be wiped anyway (found driving it, 2026-10-14).
    */
    if (read.blank || read.games.length === 0) {
      setReading(read);
      return;
    }
    setGames(gamesFromReading(read, GAMES_REQUIRED));
    setDirty(true);
    setReading(read);
  }

  const firstName = (playerId: string | null) =>
    table.players.find((p) => p.playerId === playerId)?.firstName ?? null;
  const readButEmpty = emptyButRead(reading, games);

  return frame(
    <div className="flex max-w-2xl flex-col gap-5">
      <header>
        {/*
          WHICH LEAGUE THIS IS, AND THE WAY OUT, are the league's own header above — its name, "←
          All leagues" and its tabs (owner, 2026-10-15). They replaced a trail ("League › Scores")
          that did the same job in a different place on every page. Never `navigate(-1)`: history is
          not a place (owner, 2026-09-19).
        */}
        <div className="flex items-center gap-1">
          <h1 className="font-display text-[26px] leading-tight">
            Session {sheet.data?.sheet.sessionNumber} scores
          </h1>
          {howTo.button}
        </div>
        {howTo.panel}
        {/*
          Counts tables where every player has their four games, not tables she has opened. A
          progress number that flatters is worse than none — she uses this to decide when to stop.
        */}
        <p className="tabular mt-1 text-[14px] text-ink-2">
          {sheet.data?.sheet.tablesEntered} of {tables.length} tables entered
          {sheet.data?.sheet.tablesApproved
            ? ` · ${sheet.data.sheet.tablesApproved} approved`
            : null}
        </p>
        {closedAt ? (
          <p className="mt-2 rounded-md bg-sand px-3 py-2 text-[13px] text-ink-2">
            This session is closed. Reopen it to change anything.
          </p>
        ) : null}
        {actor?.role === 'commissioner' && leagueId && sessionId && tables.some((t) => t.players.some((p) => p.games.length > 0)) ? (
          <CorrectSession leagueId={leagueId} sessionId={sessionId} sessionNumber={sheet.data?.sheet.sessionNumber ?? 0} />
        ) : null}
      </header>


      {/*
        ⚠ THE CIRCLES HAD TO BE GUESSED AT — owner, 2026-09-19: "Are the numbers at the top the
        tables?" They were, and nothing on screen said so. The only thing that named them was the
        `aria-label`, which is exactly backwards: the sighted director was the one left guessing.

        A heading now names them, and the line underneath says which one she is on and where it has
        got to — so the navy circle and the green circle stop being a colour code she has to learn.
      */}
      <section>
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3">Tables</h2>
        {/* The pager. 44px targets, because she is tapping this one-handed. */}
        <div className="-mx-5 mt-2 flex gap-2 overflow-x-auto px-5 pb-1">
          {tables.map((t) => (
          <button
            key={t.tableNumber}
            type="button"
            disabled={save.isPending || approve.isPending} onClick={() => setTableNumber(t.tableNumber)}
            aria-current={t.tableNumber === table.tableNumber}
            aria-label={`Table ${t.tableNumber}${t.state === 'approved' ? ', approved' : t.complete ? ', entered' : ''}`}
            className={[
              'flex size-11 shrink-0 items-center justify-center rounded-full border text-[15px] font-medium',
              t.tableNumber === table.tableNumber
                ? 'border-deep bg-deep text-on-deep'
                : t.complete
                  ? 'border-sage bg-tint-sage text-sage'
                  : 'border-light bg-paper text-ink-2',
            ].join(' ')}
          >
              {t.tableNumber}
            </button>
          ))}
        </div>
        <p className="mt-1 text-[13px] text-ink-2">
          <span className="font-medium text-ink">Table {table.tableNumber}</span>
          {' · '}
          {table.state === 'approved'
            ? 'approved'
            : table.complete
              ? 'entered, not approved'
              : table.players.some(player => player.games.length > 0) ? 'partially entered' : 'no scores yet'}
        </p>
      </section>

      {sheet.data?.cardReading && !closedAt && !scoringBlocked && !approvedReadOnly ? (
        <section className="flex flex-col gap-3">
          <ReadCard
            key={draftKey}
            endpoint={`/api/sessions/${sessionId}/scores/${table.tableNumber}/read-card`}
            tableNumber={table.tableNumber}
            replacing={games.some(hasAnyEntry)}
            onRead={fillFromCard}
          />
          {reading ? <ReadingNotice reading={reading} totals={totals} nameOf={firstName} then="save" /> : null}
        </section>
      ) : null}

      {/* The grid is shared with the player's card — see ScoreGrid.tsx for why it is shaped this way. */}
      <section>
        {/* Closed is already said once, at the top. Any other reason scoring is blocked is said here. */}
        {scoringBlocked && !closedAt ? <p role="status" className="text-[14px] text-ink-2">{scoringBlocked}</p> : null}
        <ScoreGrid
          columns={table.players.map((player) => ({
            playerId: player.playerId,
            /* First name; where two at the table share one, the last initial tells them apart. */
            label: table.players.some((other) => other.playerId !== player.playerId && other.firstName === player.firstName)
              ? `${player.firstName} ${player.lastName.charAt(0)}.`
              : player.firstName,
            fullName: `${player.firstName} ${player.lastName}`,
          }))}
          games={displayedGames}
          onChange={(update) => {
            setGames(update);
            setDirty(true);
          }}
          mode={sheet.data?.sheet.scoringMode}
          disabled={Boolean(approvedReadOnly || scoringBlocked || closedAt || save.isPending || draftProblem)}
          doubtful={doubtfulCells(reading)}
          readButEmpty={readButEmpty}
        />
      </section>

      {/*
        WHO HANDED THIS TABLE IN. A card a player filled in and one the director typed herself look
        identical on the page otherwise, and the whole point of review is knowing which is which.
      */}
      {table.state === 'submitted' ? (
        <p className="mt-4 rounded-md border border-light bg-sand px-4 py-3 text-[14px] text-ink-2">
          Check scores, then approve.
        </p>
      ) : table.state === 'approved' ? (
        <p className="mt-4 text-[13px] text-sage">You have approved this table.</p>
      ) : null}

      {/*
        IN THE PAGE, NOT PINNED TO THE WINDOW — owner, 2026-10-15: a footer fixed to the bottom of
        the screen "doesn't make sense". It sits under the grid, where she finishes. It once hid
        "+ Game" behind it, too.
      */}
      <div className="max-w-[34rem] border-t border-light pt-4">
        {/* `max-w-[34rem]` — the grid's width, so Save lines up under it. See the note by the table. */}
        <div className="flex max-w-[34rem] flex-col gap-3">
          {draftProblem ? <ErrorNote>{draftProblem}</ErrorNote> : null}
          {error ? <ErrorNote>{error}</ErrorNote> : null}
          {/*
            A WAY OUT OF A REFUSED SAVE. Somebody else saved this table first (a player handing the
            card in, or this same save on another device), so her draft is out of date. Without this
            the draft won on every reload and every save was refused again.
          */}
          {save.error instanceof ApiError && save.error.status === 409 && dirty ? (
            <Button
              variant="destructive"
              className="self-start"
              onClick={() => {
                if (!window.confirm('Discard your changes to this table and load the saved scores?')) return;
                if (draftKey) clearDraft(draftKey);
                setGames(gamesFromSaved(table.players)); setDirty(false); setDraftRevision(table.revision);
                setReading(null); save.reset();
                void refreshScores();
              }}
            >
              Load the saved scores
            </Button>
          ) : null}
          <div className="flex items-center gap-3">
            {/*
              Held while a column contradicts itself. The reason is already on screen, naming the
              game and the two players — a disabled button whose reason is above it is answerable;
              one with no reason anywhere is the thing this codebase refuses to ship.
            */}
            {/*
              ⚠ FULL WIDTH ON A PHONE ONLY — owner, 2026-09-19: "why is the save button so wide."

              `flex-1` stretched it across the whole 672px column, which made a quiet confirmation
              into a red slab the width of the page and broke the palette's scarcity rule at rest.

              It is `flex-1` again, but the BAR is now capped at the grid's 34rem — so it fills the
              width of the table above it and stops there, which is what he asked for: "make that
              and the save button as wide as the table."
            */}
            {table.state === 'approved' && !closedAt && !scoringBlocked && approvedReadOnly ? <Button disabled={Boolean(draftProblem)} onClick={() => { setEditingApprovedKey(draftKey); save.reset(); }}>Edit scores</Button> : <>
            {!dirty && !save.isPending && table.players.some((p) => p.games.length > 0) ? <span role="status" className="flex min-h-[48px] flex-1 items-center text-[14px] text-ink-2">Scores saved</span> : <Button
              className="min-h-[48px] flex-1"
              disabled={
                !dirty || !correctionChanged || Boolean(draftProblem) || Boolean(scoringBlocked) || save.isPending || games.length === 0 || Boolean(closedAt) || problems.length > 0 || empty.length > 0 || readButEmpty.length > 0
              }
              onClick={() => save.mutate()}
            >
              {/* Only something changed can be saved: re-saving an approved table un-approves it. */}
              {save.isPending ? 'Saving…' : `Save table ${table.tableNumber}`}
            </Button>}
            </>}
            {table.state === 'approved' && !approvedReadOnly ? <Button variant="secondary" disabled={save.isPending} onClick={() => {
              if (draftKey) clearDraft(draftKey);
              setGames(gamesFromSaved(table.players)); setDirty(false); setDraftRevision(table.revision);
              setEditingApprovedKey(null); setReading(null); setDone(null); save.reset();
            }}>Cancel</Button> : null}

            {/*
              Approving is a SEPARATE action from saving, deliberately. Saving is "these are the
              numbers"; approving is "I have read them". Folding the second into the first would
              make every correction an approval of itself.
            */}
            {table.complete && table.state !== 'approved' && !closedAt && !scoringBlocked ? (
              <Button
                variant={dirty ? 'secondary' : 'primary'}
                className="min-h-[48px]"
                disabled={approve.isPending || dirty}
                onClick={() => approve.mutate()}
              >
                {approve.isPending ? 'Approving…' : 'Approve'}
              </Button>
            ) : null}
          </div>

          {done && done.table === table.tableNumber && !dirty ? (
            <div role="status" className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span className="text-[14px] font-medium text-sage">
                {done.kind === 'approved'
                  ? `Table ${done.table} approved.`
                  : done.kind === 'reapprove'
                    ? 'Saved. Approve it again.'
                    : `Table ${done.table} saved.`}
              </span>
              {nextTable ? (
                <Button variant="secondary" onClick={() => setTableNumber(nextTable.tableNumber)}>
                  Next: Table {nextTable.tableNumber}
                </Button>
              ) : null}
            </div>
          ) : null}

          {dirty ? (
            /*
              ⚠ "THIS DEVICE", NOT "THIS PHONE" — owner, 2026-09-19: "I'm on a desktop."

              The line describes the local draft, which is `localStorage` and works the same in any
              browser. Saying "phone" was the screen telling a director on a laptop a fact about
              somebody else's machine — and worse, quietly implying her work was NOT being kept.
              The module's banner may go on talking about a phone, because that is the hardest case
              it was designed for; the INTERFACE must not assume it.
            */
            <span className="text-[12px] text-ink-3">
              Not saved yet &mdash; kept in this browser until you save
            </span>
          ) : null}

          {/*
            CLOSING THE SESSION. The refusal is shown as a sentence rather than a disabled button she
            cannot explain — and it is the SAME sentence the server would refuse with, so the two
            cannot disagree about why.
          */}
          {closedAt ? (
            <Button
              variant="secondary"
              className="min-h-[44px]"
              disabled={closeSession.isPending}
              onClick={() => closeSession.mutate(true)}
            >
              {closeSession.isPending ? 'Reopening…' : 'Reopen this session'}
            </Button>
          ) : blocked ? (
            blocked === `Table ${table.tableNumber} has not been approved.` ? null : <p className="text-[13px] text-ink-3">{blocked}</p>
          ) : (
            <Button
              variant="secondary"
              className="min-h-[44px]"
              disabled={closeSession.isPending}
              onClick={() => closeSession.mutate(false)}
            >
              {closeSession.isPending ? 'Closing…' : 'Close the session'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}


/*
 * The draft, kept in the browser.
 *
 * Wrapped because storage can be unavailable — a locked-down browser, private mode — and a score
 * screen that throws on load because it could not cache something is worse than one that simply
 * does not cache.
 */
function readDraft(key: string): Game[] | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? gamesFromStorage(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

/**
 * The revision the draft was started from, kept BESIDE the draft. It used to be in sessionStorage
 * while the draft was in localStorage, so closing the tab kept the draft and lost its revision —
 * and every save of it after that was refused as out of date.
 */
function readDraftRevision(key: string): string | undefined {
  try {
    return window.localStorage.getItem(`${key}:revision`) ?? window.sessionStorage.getItem(`${key}:revision`) ?? undefined;
  } catch {
    return undefined;
  }
}

function writeDraft(key: string, games: Game[], revision: string | undefined): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(games));
    if (revision) window.localStorage.setItem(`${key}:revision`, revision);
  } catch {
    /* Nothing to do. She keeps typing; it just will not survive a reload. */
  }
}

function clearDraft(key: string): void {
  try {
    window.localStorage.removeItem(key);
    window.localStorage.removeItem(`${key}:revision`);
    window.sessionStorage.removeItem(`${key}:revision`);
  } catch {
    /* As above. */
  }
}

/**
 * CORRECT THIS SESSION — the commissioner, in a finished league only (owner, 2026-10-26). Clears the
 * session's scores so she can fix the seating and enter every game; see `openSessionForCorrection`.
 * It cannot be undone, which is the one reason it asks first.
 */
function CorrectSession({ leagueId, sessionId, sessionNumber }: { leagueId: string; sessionId: string; sessionNumber: number }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useRef<HTMLDialogElement>(null);
  const league = useQuery({ queryKey: ['league', leagueId], queryFn: () => fetchLeague(leagueId) });
  const correct = useMutation({
    mutationFn: () => apiFetch<SessionSheetResponse>(`/api/sessions/${sessionId}/scores/correct`, { method: 'POST' }),
    onSuccess: async () => {
      confirm.current?.close();
      await queryClient.invalidateQueries();
      navigate(`/director/sessions/${sessionId}/seating`);
    },
  });
  if (league.data?.league.status !== 'completed') return null;
  return (
    <>
      <Button variant="secondary" className="mt-3 min-h-[44px]" onClick={() => { correct.reset(); confirm.current?.showModal(); }}>
        Correct this session
      </Button>
      <dialog
        ref={confirm}
        aria-labelledby="correct-session-title"
        className="m-auto w-[calc(100%-2rem)] max-w-md rounded-md border border-light bg-paper p-6 text-ink backdrop:bg-black/30"
        onCancel={(event) => { if (correct.isPending) event.preventDefault(); }}
      >
        <h2 id="correct-session-title" className="font-display text-xl">Correct session {sessionNumber}?</h2>
        <p className="mt-2 text-sm text-ink-2">
          Every score in this session is cleared so you can fix the seating and enter each game again. Other sessions are not changed. This can’t be undone.
        </p>
        {correct.error ? <ErrorNote>{correct.error instanceof ApiError ? correct.error.message : 'Could not open this session for correction.'}</ErrorNote> : null}
        <div className="mt-5 flex gap-3">
          <Button autoFocus variant="secondary" disabled={correct.isPending} onClick={() => confirm.current?.close()}>Cancel</Button>
          <Button variant="destructive" disabled={correct.isPending} onClick={() => correct.mutate()}>
            {correct.isPending ? 'Clearing…' : 'Clear scores and fix seating'}
          </Button>
        </div>
      </dialog>
    </>
  );
}
